import React, { useState, useEffect } from 'react'
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Button,
    useDisclosure,
    IconButton,
    Box,
    FormLabel,
    Flex,
    Text,
    FormControl,
    useToast,
    Input,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Radio,
    RadioGroup,
    Stack
} from '@chakra-ui/react'
import { FaLock } from 'react-icons/fa'
import { useAuth } from '../../contexts/AuthContext'
import { doc, setDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore'
import { db } from '../../utils/init-firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'

// Google API configuration
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
const SCOPES = [
  'https://www.googleapis.com/auth/chat.spaces',
  'https://www.googleapis.com/auth/chat.messages',
  'https://www.googleapis.com/auth/chat.memberships',
  'https://www.googleapis.com/auth/chat.memberships.app'
].join(' ')

// Load Google OAuth client library
const loadGoogleClient = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve(window.google.accounts.oauth2);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.oauth2) {
        resolve(window.google.accounts.oauth2);
      } else {
        reject(new Error('Google OAuth client failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Google OAuth client'));
    document.body.appendChild(script);
  });
};

export default function CreateLockRequestDrawer({ 
    loanID, 
    loanNumber, 
    fullButton,
    initialValues,
    onSubmit,
    isConfirmation = false,
    isOpen: externalIsOpen,
    onClose: externalOnClose,
    clientStatus = 'Completed',
    isDisabled = false,
    loanAmount
}) {
    const { isOpen: internalIsOpen, onOpen: internalOnOpen, onClose: internalOnClose } = useDisclosure()
    const btnRef = React.useRef()
    const { currentUser, SuperAdmin, isAdmin, submissionSettings } = useAuth()
    const toast = useToast()
    const [isCheckingSpaces, setIsCheckingSpaces] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isFormInvalid, setIsFormInvalid] = useState(true);


    // Use external state if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
    const onOpen = internalOnOpen
    const onClose = externalOnClose || internalOnClose

    const [rate, setRate] = React.useState(initialValues?.rate || '')
    const [termType, setTermType] = React.useState(initialValues?.termDays?.toString() || 'standard')
    const [customDays, setCustomDays] = React.useState(initialValues?.termDays?.toString() || '')
    const [points, setPoints] = React.useState(initialValues?.points || '')
    const [cost, setCost] = React.useState(initialValues?.cost || '')
    const [pricingType, setPricingType] = React.useState(initialValues?.pricingType || 'LP')
    const [notes, setNotes] = React.useState(initialValues?.notes || '')

    // More robust validation logic for the submit button
    useEffect(() => {
        const validateForm = () => {
            // Rate validation
            const rateValue = rate.toString().trim();
            if (rateValue === '' || isNaN(parseFloat(rateValue))) {
                return true; // Invalid
            }

            // Term Type validation - 'standard' is the initial value when no initialValues are provided
            if (termType === 'standard' || termType.trim() === '') {
                return true; // Invalid
            }
            
            // Custom Days validation (if applicable)
            if (termType === 'custom' && (customDays.trim() === '' || isNaN(parseInt(customDays, 10)) || parseInt(customDays, 10) <= 0)) {
                return true; // Invalid
            }
            
            // Pricing Type validation
            if (pricingType.trim() === '') {
                return true; // Invalid
            }

            // Points validation
            const pointsValue = points.toString().trim();
            if (pointsValue === '-' || pointsValue === '' || isNaN(parseFloat(pointsValue))) {
                return true; // Invalid
            }

            // Cost validation
            const costValue = cost.toString().trim();
            if (costValue === '-' || costValue === '' || isNaN(parseFloat(costValue))) {
                return true; // Invalid
            }

            // If all checks pass
            return false; // Valid
        };

        setIsFormInvalid(validateForm());
    }, [rate, points, cost, termType, customDays, pricingType]);


    // Handle points change
    const handlePointsChange = (value) => {
        setPoints(value); // Always update the input to what the user typed

        // If the value is empty or just a minus sign, clear the dependent field
        if (value.trim() === '' || value.trim() === '-') {
            setCost('');
            return;
        }

        const pointsNum = parseFloat(value);
        
        // If the parsed number is valid and we have a loan amount, calculate cost
        if (!isNaN(pointsNum) && loanAmount) {
            const calculatedCost = ((pointsNum * loanAmount) / 100).toFixed(2);
            setCost(calculatedCost);
        } else {
            // Otherwise, clear the cost
            setCost('');
        }
    };

    // Handle cost change
    const handleCostChange = (value) => {
        setCost(value); // Always update the input to what the user typed

        // If the value is empty or just a minus sign, clear the dependent field
        if (value.trim() === '' || value.trim() === '-') {
            setPoints('');
            return;
        }
        
        const costNum = parseFloat(value);
        
        // If the parsed number is valid and we have a loan amount, calculate points
        if (!isNaN(costNum) && loanAmount) {
            const calculatedPoints = ((costNum / loanAmount) * 100).toFixed(3);
            setPoints(calculatedPoints);
        } else {
            // Otherwise, clear points
            setPoints('');
        }
    };


    // Handle loan amount changes
    React.useEffect(() => {
        if (!loanAmount) return;

        const numericPoints = parseFloat(points);
        if (!isNaN(numericPoints)) { // Check if 'points' is a valid number first
            const newCost = ((numericPoints * loanAmount) / 100).toFixed(2);
            setCost(newCost);
        } else {
            const numericCost = parseFloat(cost);
            if (!isNaN(numericCost)) { // Else, check if 'cost' is a valid number
                const newPoints = ((numericCost / loanAmount) * 100).toFixed(3);
                setPoints(newPoints);
            }
        }
    }, [loanAmount]);

    // Reset form when drawer opens
    React.useEffect(() => {
        if (isOpen && initialValues) {
            setRate(initialValues.rate || '')
            setTermType(initialValues.termDays?.toString() || 'standard')
            setCustomDays(initialValues.termDays?.toString() || '')
            setPoints(initialValues.points || '')
            setCost(initialValues.cost || '')
            setPricingType(initialValues.pricingType || 'LP')
            setNotes(initialValues.notes || '')
        }
    }, [isOpen, initialValues])

    const checkExistingSpace = async () => {
        try {
            setIsCheckingSpaces(true)
            const functions = getFunctions(undefined, 'us-east1');
            const checkSpaces = httpsCallable(functions, 'checkGoogleChatSpaces');
            const result = await checkSpaces();
            const { spaceExists, needsPermissions } = result.data;
            console.log('Space check result:', result.data);

            if (spaceExists) {
                // Space exists, no permissions needed
                return true;
            }

            if (needsPermissions) {
                // We need user permissions to create space
                return false;
            }

            return false;
        } catch (error) {
            console.error('Error checking existing space:', error);
            return false;
        } finally {
            setIsCheckingSpaces(false)
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)
        
        const termDays = termType === 'custom' ? parseInt(customDays) : parseInt(termType)
        const requestData = {
            rate: parseFloat(rate),
            termDays: termDays,
            points: parseFloat(points),
            cost: parseFloat(cost),
            pricingType: pricingType,
            notes: notes
        }

        if (isConfirmation && onSubmit) {
            onSubmit(requestData)
            setIsSubmitting(false)
            return
        }

        try {
            // Only check spaces and request permissions if chat notification is enabled
            const chatEnabled = submissionSettings?.['Rate Locked Notifications']?.['Sent Chat Message to LO']
            if (chatEnabled) {
                // Check if space already exists first
                const spaceExists = await checkExistingSpace();
                if (spaceExists) {
                    // If space exists, we can just submit the request
                    await submitRequest(requestData);
                    setIsSubmitting(false)
                    return;
                }

                // Load Google OAuth client if not already loaded
                await loadGoogleClient();

                // Initialize token client
                const client = window.google.accounts.oauth2.initTokenClient({
                    client_id: CLIENT_ID,
                    scope: SCOPES,
                    callback: async (response) => {
                        if (response.error) {
                            toast({
                                title: 'Error requesting permissions',
                                description: response.error,
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                            })
                            setIsSubmitting(false)
                            return
                        }

                        if (!response.access_token) {
                            toast({
                                title: 'Permissions Required',
                                description: 'You must grant permissions to request a rate lock.',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                            })
                            setIsSubmitting(false)
                            return
                        }

                        // Check if the required scopes were granted
                        const grantedScopes = response.scope.split(' ')
                        const requiredScopes = SCOPES.split(' ')
                        const hasAllScopes = requiredScopes.every(scope => grantedScopes.includes(scope))

                        if (!hasAllScopes) {
                            toast({
                                title: 'Permissions Required',
                                description: 'You must grant all required permissions to request a rate lock.',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                            })
                            setIsSubmitting(false)
                            return
                        }
                        
                        // Store the access token
                        await setDoc(doc(db, 'users', currentUser.uid), {
                            accessToken: response.access_token,
                            tokenExpiry: Date.now() + (response.expires_in * 1000)
                        }, { merge: true })

                        // Submit the request
                        await submitRequest(requestData)
                        setIsSubmitting(false)
                    }
                })
                
                client.requestAccessToken({
                    prompt: 'select_account' // Show a simple accept/deny prompt
                })
            } else {
                // If chat notification is not enabled, just submit the request
                await submitRequest(requestData)
                setIsSubmitting(false)
            }
        } catch (error) {
            console.error('Error submitting request:', error)
            toast({
                title: 'Error submitting request',
                description: error.message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            })
            setIsSubmitting(false)
        }
    }

    const submitRequest = async (requestData) => {
        // Create the rate lock request
        const newDocRef = doc(collection(db, 'RateLockRequests'))
        await setDoc(newDocRef, {
            id: newDocRef.id,
            loanID: loanID,
            loanNumber: loanNumber,
            ...requestData,
            status: 'Pending',
            clientStatus: clientStatus?.toLowerCase().replace(' ','-'),
            'Date Created': serverTimestamp(),
            requestedBy: {
                email: currentUser.email,
                name: currentUser.displayName,
                photoURL: currentUser.photoURL,
                uid: currentUser.uid
            }
        })

        onClose()
        toast({
            title: 'Rate Lock Request Submitted!',
            description: 'The Lock Desk team will review your request.',
            status: 'success',
            position: 'top',
            duration: 9000,
            isClosable: true,
        })
    }

    return (
        <>
            {!isConfirmation && (
                fullButton ? 
                    <Button 
                        size='sm' 
                        ref={btnRef} 
                        colorScheme='blue' 
                        onClick={onOpen} 
                        leftIcon={<FaLock/>}
                        isDisabled={isDisabled || isCheckingSpaces}
                        isLoading={isCheckingSpaces}
                    >
                        Request Rate Lock
                    </Button>
                :
                    <IconButton 
                        size='sm' 
                        icon={<FaLock size={18}/>}
                        ref={btnRef} 
                        colorScheme='blue' 
                        onClick={onOpen} 
                        isDisabled={isDisabled || isCheckingSpaces}
                        isLoading={isCheckingSpaces}
                    />
            )}
            
            <Drawer
                isOpen={isOpen}
                placement='right'
                onClose={onClose}
                finalFocusRef={btnRef}
                size='md'
            >
                <DrawerOverlay>
                    <Box
                        borderWidth={{'base': 'none', 'md': "1px"}}
                        rounded="lg"
                        shadow={{'base': 'none', 'md': "1px 1px 3px rgba(0,0,0,0.3)"}}
                        p={{'base': '0', 'md': "5"}}
                        m="10px auto"
                        as="form"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DrawerContent>
                            <DrawerCloseButton />
                            <DrawerHeader>
                                {isConfirmation ? 'Confirm Rate Lock Request' : 'Request Rate Lock'}
                            </DrawerHeader>
                            
                            <DrawerBody>
                                {loanNumber && <Text mb='4'>Loan #: {loanNumber}</Text>}
                                
                                <FormControl isRequired mb={4}>
                                    <FormLabel>Rate (%)</FormLabel>
                                    <NumberInput 
                                        min={0} 
                                        max={100} 
                                        precision={3} 
                                        step={0.125}
                                        value={rate}
                                        onChange={(value) => setRate(value)}
                                    >
                                        <NumberInputField placeholder="Enter rate" />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>

                                <FormControl isRequired mb={4}>
                                    <FormLabel>Lock Term (Days)</FormLabel>
                                    <RadioGroup value={termType} onChange={setTermType}>
                                        <Stack direction='row' spacing={4}>
                                            <Radio value='15'>15</Radio>
                                            <Radio value='30'>30</Radio>
                                            <Radio value='45'>45</Radio>
                                            <Radio value='60'>60</Radio>
                                            <Radio value='custom'>Custom</Radio>
                                        </Stack>
                                    </RadioGroup>
                                    
                                    {termType === 'custom' && (
                                        <NumberInput 
                                            mt={2}
                                            min={1} 
                                            max={90}
                                            value={customDays}
                                            onChange={(value) => setCustomDays(value)}
                                        >
                                            <NumberInputField placeholder="Enter number of days" />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    )}
                                </FormControl>

                                <FormControl isRequired mb={4}>
                                    <FormLabel>Pricing Type</FormLabel>
                                    <RadioGroup value={pricingType} onChange={setPricingType}>
                                        <Stack direction='row' spacing={4}>
                                            <Radio value='LP'>LP</Radio>
                                            <Radio value='BP'>BP</Radio>
                                        </Stack>
                                    </RadioGroup>
                                </FormControl>

                                <FormControl isRequired mb={4}>
                                    <FormLabel>Points</FormLabel>
                                    <NumberInput
                                        precision={3}
                                        step={0.125}
                                        value={points}
                                        onChange={handlePointsChange}
                                    >
                                        <NumberInputField
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Enter points"
                                        />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>

                                <FormControl isRequired mb={4}>
                                    <FormLabel>Cost ($)</FormLabel>
                                    <NumberInput
                                        precision={2}
                                        step={100}
                                        value={cost}
                                        onChange={handleCostChange}
                                    >
                                        <NumberInputField
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="Enter cost"
                                        />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>

                                <FormControl mb={4}>
                                    <FormLabel>Notes</FormLabel>
                                    <Input
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add any notes about this rate lock request"
                                    />
                                </FormControl>
                            </DrawerBody>

                            <DrawerFooter>
                                <Button variant='outline' mr={3} onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button type='submit' colorScheme='blue'
                                    isLoading={isSubmitting}
                                    isDisabled={isSubmitting || isFormInvalid}
                                >
                                    {isConfirmation ? 'Confirm Request' : 'Submit Request'}
                                </Button>
                            </DrawerFooter>
                        </DrawerContent>
                    </Box>
                </DrawerOverlay>
            </Drawer>
        </>
    )
}

